import { useEffect, useRef, useState } from 'react';
import { Tab } from 'tea-component';

/**
 * tab页签Hook
 * @param tabs 页签配置数组
 * @param defaultActiveId 默认激活的页签ID
 * @param storageKey 本地存储的key, 支持持久化，默认不支持，需要配置
 * @returns 返回页签相关状态和方法
 */
export const useTabTemplate = (
  {
    tabs = [],
    defaultActiveId = '',
    storageKey,
    onTabChangeCallback,
  }: {
    tabs: Tab[];
    defaultActiveId?: string;
    storageKey?: string;
    onTabChangeCallback?: () => void;
  }
) => {
  const [activeId, setActiveId] = useState('');
  const storageDefaultKey: string =
    storageKey || (location.pathname.split('/')?.pop() ?? '');

  // 初始化时直接命中有效页签
  useEffect(() => {
    const validTab = getValidTab?.() as string;
    if (validTab && validTab !== activeId) {
      onTabChange(validTab);
    }
  }, [tabs]);

  // 外部传入 defaultActiveId 变化时同步
  // 首次挂载跳过，避免覆盖 sessionStorage 中已持久化的 tab
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!defaultActiveId) return;
    const isValidTab =
      !tabs?.length || tabs.some((tab) => tab.id === defaultActiveId);
    if (!isValidTab || defaultActiveId === activeId) return;
    onTabChange(defaultActiveId);
  }, [defaultActiveId]);
  
  // 校验并获取有效页签
  // 优先级：defaultActiveId（父级传入时, 如 CopilotChatTabs 从 memoryStore 获取的值）>
  //         sessionStorage（持久化）> 默认第一个 tab
  const getValidTab = () => {
    // 当父级传入 defaultActiveId 时优先使用
    if (defaultActiveId) {
      const isValid = !tabs?.length || tabs.some((tab) => tab.id === defaultActiveId);
      if (isValid) return defaultActiveId;
    }
    // 否则从 sessionStorage 读取持久化的值作为 fallback
    const tabId = sessionStorage.getItem(storageDefaultKey);
    return tabId || defaultActiveId || tabs?.[0]?.id;
  };

  // 切换页签
  const onTabChange = (id: string) => {
    storageKey && sessionStorage.setItem(storageDefaultKey, id);
    setActiveId(id);
    // 切换回调
    onTabChangeCallback?.();
  };

  return {
    activeId,
    tabs,
    onTabChange,
  };
};
